import { useAuth } from "./useAuth";
import { hasModuleAccess } from "./accessControl";
import UnsavedChangesBoundary from "./components/common/UnsavedChangesBoundary";
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { createAction, deleteAction, getActions } from "./api/actions.api.js";
import ActionDetailDrawer from "./ActionDetailDrawer";
import ActionForm from "./ActionForm";
import ActionsHealthPanel from "./components/actions/ActionsHealthPanel";
import { Button, Input, PageState, Select, StatusBadge, Table } from "./ui";
import ModalA11yWrapper from "./ui/ModalA11yWrapper";
import DestructiveConfirm from "./components/common/DestructiveConfirm";
import { useActionCategoryOptions } from "./hooks/useActionCategoryOptions";
import { colors, spacing, typography } from "./ui/tokens";
import { ACTION_STATUS_OPTIONS, getActionDisplayStatus } from "./utils/actionStatus";
import "./ActionsDashboard.css";

const normalizeError = (err) =>
  err?.detail || err?.message || (typeof err === "string" ? err : "Failed to load actions");

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "-");
const ACTION_PRIORITY_OPTIONS = ["", "Low", "Medium", "High", "Critical"];
const ACTIONS_BASE_ROUTE = "/actions";
const ACTION_STATUS_FILTER_OPTIONS = [{ value: "All", label: "All" }, ...ACTION_STATUS_OPTIONS];

const ACTION_TABLE_COLUMN_OPTIONS = [
  { id: "title", label: "Action" },
  { id: "status", label: "Status" },
  { id: "priority", label: "Priority" },
  { id: "category", label: "Category" },
  { id: "due_date", label: "Due" },
  { id: "owner", label: "Owner" },
  { id: "assignee", label: "Assignee" },
  { id: "actions", label: "Actions" },
];

const flattenActionRows = (actions = []) => {
  const list = Array.isArray(actions) ? actions : [];
  const childrenMap = list.reduce((acc, action) => {
    if (action?.parent_action_id) {
      const key = Number(action.parent_action_id);
      const children = acc.get(key) || [];
      children.push(action);
      acc.set(key, children);
    }
    return acc;
  }, new Map());

  const rows = [];
  const seen = new Set();

  const visit = (action, depth = 0, parentTitle = "") => {
    if (!action?.id || seen.has(Number(action.id))) return;
    seen.add(Number(action.id));
    rows.push({ ...action, depth, parentTitle });
    const children = childrenMap.get(Number(action.id)) || [];
    children.forEach((child) => visit(child, depth + 1, action.title || action.name || `Action #${action.id}`));
  };

  list.filter((action) => !action?.parent_action_id).forEach((action) => visit(action, 0, ""));
  list.filter((action) => !seen.has(Number(action?.id))).forEach((action) => visit(action, 0, ""));

  return rows;
};

const getTodayDateInputValue = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60 * 1000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
};

export default function ActionsDashboard() {
  const { user } = useAuth();
  const canEdit = hasModuleAccess(user, "actions", "EDIT");
  const canManage = hasModuleAccess(user, "actions", "MANAGE");
  const navigate = useNavigate();
  const location = useLocation();
  const { actionId: routeActionId } = useParams();
  const [activeTab, setActiveTab] = useState("actions");
  const [actions, setActions] = useState([]);
  const [drawerActionId, setDrawerActionId] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createParentId, setCreateParentId] = useState(null);
  const [filters, setFilters] = useState({ q: "", status: "All", category: "All" });
  const [healthFilters, setHealthFilters] = useState({
    search: "",
    status: "",
    category: "",
    priority: "",
    owner: "",
    assignee: "",
    asOf: getTodayDateInputValue(),
  });
  const [healthRefreshNonce, setHealthRefreshNonce] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [columnOrder, setColumnOrder] = useState([
    "title",
    "status",
    "priority",
    "category",
    "due_date",
    "owner",
    "assignee",
    "actions",
  ]);
  const [visibleColumnIds, setVisibleColumnIds] = useState(ACTION_TABLE_COLUMN_OPTIONS.map((column) => column.id));
  const [columnWidths, setColumnWidths] = useState({
    title: 300,
    status: 140,
    priority: 120,
    category: 140,
    due_date: 120,
    owner: 140,
    assignee: 140,
    actions: 170,
  });
  const createTitleId = useId();
  const columnPickerId = useId();
  const columnPickerRef = useRef(null);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const categoryOptions = useActionCategoryOptions(filters.category === "All" ? "" : filters.category);
  const isActionRoute = location.pathname === ACTIONS_BASE_ROUTE || location.pathname.startsWith(`${ACTIONS_BASE_ROUTE}/`);
  const resolvedRouteActionId = routeActionId ? Number(routeActionId) : null;
  const selectedActionId = resolvedRouteActionId || drawerActionId;

  const loadActions = useCallback(async () => {
    try {
      setLoadingList(true);
      setError(null);
      const params = {};
      if (filters.q.trim()) params.q = filters.q.trim();
      if (filters.status && filters.status !== "All") params.status = filters.status;
      if (filters.category && filters.category !== "All") params.category = filters.category;
      params.limit = 500;

      const res = await getActions(params);
      const payload = res?.data ?? res;
      setActions(Array.isArray(payload?.items) ? payload.items : Array.isArray(payload) ? payload : []);
    } catch (err) {
      setError(normalizeError(err));
    } finally {
      setLoadingList(false);
    }
  }, [filters]);

  useEffect(() => {
    loadActions();
  }, [loadActions]);

  useEffect(() => {
    if (resolvedRouteActionId) {
      setDrawerActionId(null);
    }
  }, [resolvedRouteActionId]);

  useEffect(() => {
    const handler = () => {
      setCreateParentId(null);
      setShowCreate(true);
    };
    window.addEventListener("openCreateAction", handler);
    return () => window.removeEventListener("openCreateAction", handler);
  }, []);

  useEffect(() => {
    if (!showColumnPicker) return undefined;

    const handlePointerDown = (event) => {
      if (columnPickerRef.current && !columnPickerRef.current.contains(event.target)) {
        setShowColumnPicker(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setShowColumnPicker(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showColumnPicker]);

  const handleCreate = async (payload) => {
    if (!canEdit) return;
    try {
      const created = await createAction(payload);
      await loadActions();
      setShowCreate(false);
      setCreateParentId(null);
      const newId = created?.data?.id || created?.id;
      if (newId) {
        handleSelectAction(Number(newId));
      }
    } catch (err) {
      setError(normalizeError(err));
      return false;
    }
  };

  const handleDelete = async (id) => {
    if (!canManage) return;
    await deleteAction(id);
    if (Number(selectedActionId) === Number(id)) {
      handleCloseDrawer();
    }
    await loadActions();
  };

  const handleSelectAction = useCallback(
    (id) => {
      const normalizedId = Number(id);
      if (!normalizedId) return;
      if (isActionRoute) {
        navigate(`${ACTIONS_BASE_ROUTE}/${normalizedId}`);
        return;
      }
      setDrawerActionId(normalizedId);
    },
    [isActionRoute, navigate]
  );

  const handleCloseDrawer = useCallback(() => {
    if (resolvedRouteActionId && isActionRoute) {
      navigate(ACTIONS_BASE_ROUTE);
      return;
    }
    setDrawerActionId(null);
  }, [isActionRoute, navigate, resolvedRouteActionId]);

  const handleOpenActionPage = useCallback(
    (id) => {
      const normalizedId = Number(id);
      if (!normalizedId) return;
      navigate(`${ACTIONS_BASE_ROUTE}/${normalizedId}`);
    },
    [navigate]
  );

  const requestDelete = (action) => {
    if (!action?.id) return;
    setConfirmDelete({
      id: action.id,
      title: action.title || action.name || "this action",
    });
  };

  const rows = useMemo(() => flattenActionRows(actions), [actions]);
  const hasFilters = Boolean(filters.q.trim() || filters.status !== "All" || filters.category !== "All");
  const orderedColumnOptions = useMemo(() => {
    const byId = new Map(ACTION_TABLE_COLUMN_OPTIONS.map((column) => [column.id, column]));
    return columnOrder.map((columnId) => byId.get(columnId)).filter(Boolean);
  }, [columnOrder]);
  const handleHealthFilterChange = useCallback(
    (field) => (value) => {
      setHealthFilters((current) => ({
        ...current,
        [field]: value,
      }));
    },
    []
  );
  const handleHealthRefresh = useCallback(() => {
    setHealthRefreshNonce((current) => current + 1);
  }, []);

  const handleToggleColumnVisibility = (columnId) => {
    setVisibleColumnIds((current) => {
      if (current.includes(columnId)) {
        if (current.length === 1) return current;
        return current.filter((id) => id !== columnId);
      }
      return [...current, columnId];
    });
  };

  return (
    <div style={{ display: "grid", gap: spacing.xl }}>
      <div style={headerStyle}>
        <div>
          <h2 style={{ margin: 0 }}>Actions</h2>
          <p style={subtitleStyle}>
            {activeTab === "actions"
              ? "Track owners, due dates, and completion status from a single register."
              : "Review action health from within the actions workspace."}
          </p>
        </div>
        <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap" }}>
          {activeTab === "actions" && (
            <Button disabled={!canEdit} onClick={() => setShowCreate(true)} variant="primary">
              Create Action
            </Button>
          )}
        </div>
      </div>

      <div style={tabRowStyle} role="tablist" aria-label="Actions views">
        {[
          { key: "actions", label: "Actions Register" },
          { key: "health", label: "Actions Health" },
        ].map((tab) => (
          <Button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            variant="ghost"
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: `${spacing.sm}px ${spacing.md}px`,
              borderBottom: activeTab === tab.key ? `2px solid ${colors.accent}` : "2px solid transparent",
              borderRadius: 0,
              color: activeTab === tab.key ? colors.accent : "#475467",
              boxShadow: "none",
            }}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {activeTab === "actions" && (
        <>
          <div style={toolbarStyle}>
            <div style={toolbarHeaderRowStyle}>
              <div style={toolbarIntroStyle}>
                <div style={toolbarTitleStyle}>Filter actions</div>
                <div style={toolbarHintStyle}>Refine the register, then adjust visible columns when needed.</div>
              </div>

              <div style={toolbarUtilityRowStyle}>
                <Button type="button" variant="ghost" onClick={loadActions}>
                  Refresh
                </Button>
                <div ref={columnPickerRef} style={columnPickerWrapStyle}>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowColumnPicker((current) => !current)}
                    aria-expanded={showColumnPicker}
                    aria-controls={columnPickerId}
                  >
                    Columns
                  </Button>
                  {showColumnPicker && (
                    <div id={columnPickerId} style={columnPickerStyle}>
                      <div style={columnPickerTitleStyle}>Show columns</div>
                      <div style={columnPickerListStyle}>
                        {orderedColumnOptions.map((column) => {
                          const checked = visibleColumnIds.includes(column.id);
                          return (
                            <label key={column.id} style={columnOptionStyle}>
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={checked && visibleColumnIds.length === 1}
                                onChange={() => handleToggleColumnVisibility(column.id)}
                              />
                              <span>{column.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={filterRowStyle}>
              <Input
                label="Search actions"
                value={filters.q}
                onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
                placeholder="Search actions"
                style={{ ...toolbarSearchFieldStyle, flex: "1 1 280px" }}
              />
              <Select
                label="Status"
                value={filters.status}
                onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                style={toolbarFilterFieldStyle}
              >
                {ACTION_STATUS_FILTER_OPTIONS.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </Select>
              <Select
                label="Category"
                value={filters.category}
                onChange={(e) => setFilters((prev) => ({ ...prev, category: e.target.value }))}
                style={toolbarFilterFieldStyle}
              >
                {["All", ...categoryOptions].map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {error ? (
            <PageState
              state="error"
              align="left"
              title="Actions are unavailable"
              description={`${error} Refresh the register to try again.`}
              action={
                <Button type="button" variant="secondary" onClick={loadActions}>
                  Refresh
                </Button>
              }
            />
          ) : loadingList ? (
            <PageState
              state="loading"
              align="left"
              title="Loading actions"
              description="Glyder is preparing the actions register and linked ownership details."
            />
          ) : rows.length === 0 && !hasFilters ? (
            <PageState
              state="empty"
              align="left"
              title="Create an action for a high-priority item"
              description="Actions keep remediation visible with owners, due dates, and follow-up activity tied to the work that matters most."
              actions={
                <>
                  <Button type="button" onClick={() => setShowCreate(true)} variant="primary">
                    Create Action
                  </Button>
                  <Button type="button" onClick={loadActions} variant="secondary">
                    Refresh
                  </Button>
                </>
              }
            />
          ) : rows.length === 0 && hasFilters ? (
            <PageState
              state="empty"
              align="left"
              title="No actions match the current filters"
              description="Adjust the search, status, or category filters to broaden the register."
              actions={
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setFilters({ q: "", status: "All", category: "All" })}
                  >
                    Clear Filters
                  </Button>
                  <Button type="button" variant="primary" onClick={() => setShowCreate(true)}>
                    Create Action
                  </Button>
                </>
              }
            />
          ) : (
            <ActionTable
              rows={rows}
              selectedId={selectedActionId}
              columnOrder={columnOrder}
              visibleColumnIds={visibleColumnIds}
              columnWidths={columnWidths}
              onColumnReorder={(sourceId, targetId) => {
                setColumnOrder((prev) => reorderColumnIds(prev, sourceId, targetId));
              }}
              onColumnResize={(columnId, width) => {
                setColumnWidths((prev) => ({ ...prev, [columnId]: width }));
              }}
              onSelect={handleSelectAction}
              canEdit={canEdit}
              canManage={canManage}
              onDelete={requestDelete}
              onAddChild={(id) => {
                setCreateParentId(id);
                setShowCreate(true);
              }}
            />
          )}
        </>
      )}

      {activeTab === "health" && (
        <ActionsHealthPanel
          active
          filters={healthFilters}
          filterContent={(
            <div className="actions-health-filter-grid">
              <div className="actions-health-filter-field actions-health-filter-field--search">
                <Input
                  label="Search actions"
                  value={healthFilters.search}
                  onChange={(e) => handleHealthFilterChange("search")(e.target.value)}
                  placeholder="Search actions"
                />
              </div>
              <div className="actions-health-filter-field actions-health-filter-field--medium">
                <Select
                  label="Status"
                  value={healthFilters.status}
                  onChange={(e) => handleHealthFilterChange("status")(e.target.value)}
                >
                  <option value="">All statuses</option>
                  {ACTION_STATUS_OPTIONS.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="actions-health-filter-field actions-health-filter-field--medium">
                <Select
                  label="Priority"
                  value={healthFilters.priority}
                  onChange={(e) => handleHealthFilterChange("priority")(e.target.value)}
                >
                  <option value="">All priorities</option>
                  {ACTION_PRIORITY_OPTIONS.filter(Boolean).map((priority) => (
                    <option key={priority} value={priority}>
                      {priority}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="actions-health-filter-field actions-health-filter-field--date">
                <Input
                  label="As at"
                  type="date"
                  value={healthFilters.asOf}
                  max={getTodayDateInputValue()}
                  onChange={(e) => handleHealthFilterChange("asOf")(e.target.value)}
                />
              </div>
              <div className="actions-health-filter-field actions-health-filter-field--wide-medium">
                <Select
                  label="Category"
                  value={healthFilters.category}
                  onChange={(e) => handleHealthFilterChange("category")(e.target.value)}
                >
                  <option value="">All categories</option>
                  {categoryOptions.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="actions-health-filter-field actions-health-filter-field--wide-medium">
                <Input
                  label="Owner"
                  value={healthFilters.owner}
                  onChange={(e) => handleHealthFilterChange("owner")(e.target.value)}
                  placeholder="Owner"
                />
              </div>
              <div className="actions-health-filter-field actions-health-filter-field--wide-medium">
                <Input
                  label="Assignee"
                  value={healthFilters.assignee}
                  onChange={(e) => handleHealthFilterChange("assignee")(e.target.value)}
                  placeholder="Assignee"
                />
              </div>
            </div>
          )}
          refreshNonce={healthRefreshNonce}
          onRefresh={handleHealthRefresh}
          onOpenAction={handleSelectAction}
        />
      )}

      <ActionDetailDrawer
        actionId={selectedActionId}
        onClose={handleCloseDrawer}
        onActionUpdated={loadActions}
        onOpenFullPage={!resolvedRouteActionId ? handleOpenActionPage : undefined}
        onCreateChild={() => {
          setCreateParentId(selectedActionId || null);
          setShowCreate(true);
        }}
      />

      {showCreate && canEdit && (
        <UnsavedChangesBoundary onClose={() => setShowCreate(false)} itemName="action">
          {(requestClose) => (
        <ModalA11yWrapper open={showCreate} onClose={requestClose} labelledBy={createTitleId}>
          <div style={modalStyle}>
            <div style={modalHeaderStyle}>
              <h3 id={createTitleId} style={{ margin: 0 }}>Create Action</h3>
              <Button
                onClick={requestClose}
                variant="ghost"
                style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 16 }}
                aria-label="Close"
              >
                x
              </Button>
            </div>
            <ActionForm onCancel={requestClose} onSave={handleCreate} actions={actions} defaultParentActionId={createParentId} />
          </div>
        </ModalA11yWrapper>
          )}
        </UnsavedChangesBoundary>
      )}

      <DestructiveConfirm
        actionId="ACTION_DELETE"
        open={Boolean(confirmDelete)}
        resourceDisplayName={confirmDelete?.title}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (!confirmDelete) return;
          handleDelete(confirmDelete.id);
          setConfirmDelete(null);
        }}
      />
    </div>
  );
}

function ActionTable({
  rows,
  selectedId,
  columnOrder,
  visibleColumnIds,
  columnWidths,
  onColumnReorder,
  onColumnResize,
  onSelect,
  onDelete,
  onAddChild,
  canEdit,
  canManage,
}) {
  const columns = useMemo(() => {
    const allColumns = [
    {
      id: "title",
      header: "Action",
      minWidth: 220,
      accessor: (row) => (
        <div style={{ display: "grid", gap: 2, paddingLeft: row.depth * 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: spacing.xs, fontWeight: 600 }}>
            {row.depth > 0 && <span style={{ color: colors.textMuted }}>{">"}</span>}
            <span>{row.title || row.name || `Action #${row.id}`}</span>
          </div>
          <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>
            {row.depth > 0 ? `Child of ${row.parentTitle || `Action #${row.parent_action_id}`}` : `Action #${row.id}`}
          </div>
        </div>
      ),
    },
    {
      id: "status",
      header: "Status",
      accessor: (row) => {
        const displayStatus = getActionDisplayStatus(row);
        return <StatusBadge variant="status" value={displayStatus} fallbackLabel={displayStatus} />;
      },
      minWidth: 120,
    },
    {
      id: "priority",
      header: "Priority",
      accessor: (row) => <PriorityBadge value={row.priority} />,
      minWidth: 110,
    },
    {
      id: "category",
      header: "Category",
      accessor: (row) => row.category || "-",
      minWidth: 130,
    },
    {
      id: "due_date",
      header: "Due",
      accessor: (row) => formatDate(row.due_date),
      minWidth: 110,
    },
    {
      id: "owner",
      header: "Owner",
      accessor: (row) => row.owner || "-",
      minWidth: 130,
    },
    {
      id: "assignee",
      header: "Assignee",
      accessor: (row) => row.assignee || "-",
      minWidth: 130,
    },
    {
      id: "actions",
      header: "Actions",
      accessor: (row) => (
        <div style={{ display: "flex", gap: spacing.xs, flexWrap: "wrap" }}>
          <Button
            type="button"
            variant="secondary"
            disabled={!canEdit}
            onClick={(event) => {
              event.stopPropagation();
              if (canEdit) onAddChild(row.id);
            }}
            style={tableActionButtonStyle}
          >
            Add Child
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={!canManage}
            onClick={(event) => {
              event.stopPropagation();
              if (canManage) onDelete(row);
            }}
            style={tableActionButtonStyle}
          >
            Delete
          </Button>
        </div>
      ),
      minWidth: 160,
    },
    ];

    const byId = new Map(allColumns.map((column) => [column.id, column]));
    return (Array.isArray(columnOrder) ? columnOrder : allColumns.map((column) => column.id))
      .filter((columnId) => !Array.isArray(visibleColumnIds) || visibleColumnIds.includes(columnId))
      .map((columnId) => byId.get(columnId))
      .filter(Boolean);
  }, [canEdit, canManage, columnOrder, onAddChild, onDelete, visibleColumnIds]);

  return (
    <Table
      columns={columns}
      data={rows}
      draggableColumns
      resizableColumns
      columnWidths={columnWidths}
      onColumnReorder={onColumnReorder}
      onColumnResize={onColumnResize}
      emptyState="No actions found."
      onRowClick={(row) => onSelect(row.id)}
      getRowKey={(row) => row.id}
      getRowProps={(row) => ({
        "aria-label": `Open action ${row.title || row.name || row.id}`,
        "aria-selected": Number(selectedId) === Number(row.id),
        style: Number(selectedId) === Number(row.id) ? selectedRowStyle : undefined,
      })}
      caption="Actions register"
    />
  );
}

function PriorityBadge({ value }) {
  const normalized = String(value || "").trim();
  if (!normalized) return "-";
  return <StatusBadge variant="severity" value={normalized} fallbackLabel={normalized} label={normalized} />;
}

function reorderColumnIds(currentOrder, sourceId, targetId) {
  if (!Array.isArray(currentOrder)) return currentOrder;
  if (!sourceId || !targetId || sourceId === targetId) return currentOrder;
  const next = [...currentOrder];
  const sourceIndex = next.indexOf(sourceId);
  const targetIndex = next.indexOf(targetId);
  if (sourceIndex === -1 || targetIndex === -1) return currentOrder;
  const [moved] = next.splice(sourceIndex, 1);
  next.splice(targetIndex, 0, moved);
  return next;
}

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: spacing.md,
  flexWrap: "wrap",
};

const subtitleStyle = {
  margin: `${spacing.xs}px 0 0`,
  color: colors.textMuted,
  fontSize: typography.size.sm,
};

const filterRowStyle = {
  display: "flex",
  gap: spacing.md,
  alignItems: "flex-end",
  flexWrap: "wrap",
  width: "100%",
};

const toolbarStyle = {
  display: "grid",
  gap: spacing.md,
  padding: spacing.xl,
  border: `1px solid ${colors.border}`,
  borderRadius: 16,
  background: colors.surface,
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
};

const toolbarHeaderRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: spacing.md,
  flexWrap: "wrap",
};

const toolbarIntroStyle = {
  display: "grid",
  gap: spacing.xs,
};

const toolbarTitleStyle = {
  fontSize: typography.size.sm,
  fontWeight: typography.weight.bold,
  color: colors.textPrimary,
};

const toolbarHintStyle = {
  color: colors.textMuted,
  fontSize: typography.size.xs,
};

const toolbarUtilityRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: spacing.sm,
  flexWrap: "wrap",
};

const toolbarSearchFieldStyle = {
  minWidth: 260,
};

const toolbarFilterFieldStyle = {
  width: 180,
  maxWidth: "100%",
};

const tabRowStyle = {
  display: "flex",
  gap: spacing.xs,
  borderBottom: `1px solid ${colors.border}`,
};

const columnPickerWrapStyle = {
  position: "relative",
};

const columnPickerStyle = {
  position: "absolute",
  top: "calc(100% + 8px)",
  right: 0,
  zIndex: 20,
  width: 220,
  padding: 12,
  borderRadius: 12,
  border: `1px solid ${colors.border}`,
  background: colors.surface,
  boxShadow: "0 12px 30px rgba(15, 23, 42, 0.14)",
};

const columnPickerTitleStyle = {
  fontSize: typography.size.sm,
  fontWeight: typography.weight.bold,
  marginBottom: spacing.sm,
};

const columnPickerListStyle = {
  display: "grid",
  gap: spacing.xs,
};

const columnOptionStyle = {
  display: "flex",
  alignItems: "center",
  gap: spacing.sm,
  fontSize: typography.size.sm,
};

const modalStyle = {
  background: "white",
  borderRadius: 10,
  padding: 16,
  width: "520px",
  maxHeight: "90vh",
  overflowY: "auto",
};

const modalHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 8,
};

const tableActionButtonStyle = {
  padding: "4px 8px",
  borderRadius: 6,
  boxShadow: "none",
};

const selectedRowStyle = {
  background: colors.tableRowSelected,
  boxShadow: `inset 3px 0 0 ${colors.tableRowSelectedBorder}`,
};
